import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader, GripHorizontal, Maximize2, Minimize2, Image, RefreshCw } from 'lucide-react';
import { ai, recipes as recipeApi, user as userApi, chats as chatsApi } from '../utils/api';
import ReactMarkdown from 'react-markdown';
import FoodPreferences from './FoodPreferences';
import { chatMarkdownComponents } from './MarkdownRenderers';

// Parse AI-generated recipe metadata from message
// Format: <!-- RECIPE_META:{"action":"update|new|none","title":"Recipe Title"} -->
const parseRecipeMeta = (message) => {
  if (!message) return null;
  const match = message.match(/<!-- RECIPE_META:(\{.*?\}) -->/);
  if (match) {
    try {
      return JSON.parse(match[1]);
    } catch (e) {
      console.warn('Failed to parse recipe metadata:', e);
      return null;
    }
  }
  return null;
};

// Strip metadata comment from message for display
const stripRecipeMeta = (message) => {
  if (!message) return message;
  return message.replace(/\s*<!-- RECIPE_META:\{.*?\} -->\s*/g, '').trim();
};

// Fun loading phrases - simple rotation
const loadingPhrases = [
  "Thinking...",
  "Marinating...",
  "Tasting the Recipe...",
];

// Hook for rotating loading phrases every 5 seconds
const useRotatingPhrase = (isActive) => {
  const [phrase, setPhrase] = useState(loadingPhrases[0]);
  const indexRef = useRef(0);
  
  useEffect(() => {
    if (!isActive) {
      indexRef.current = 0;
      setPhrase(loadingPhrases[0]);
      return;
    }
    
    // Start with first phrase
    indexRef.current = 0;
    setPhrase(loadingPhrases[0]);
    
    // Rotate every 5 seconds
    const interval = setInterval(() => {
      indexRef.current = (indexRef.current + 1) % loadingPhrases.length;
      setPhrase(loadingPhrases[indexRef.current]);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isActive]);
  
  return phrase;
};

export const detectRecipeAction = (message, userMessage, currentRecipeTitle = '') => {
  const lowerMessage = message.toLowerCase();
  const lowerUserMsg = userMessage.toLowerCase();
  
  const hasRecipeContent = message.includes('**Ingredients:**') || 
                           message.includes('**Instructions:**') ||
                           (lowerMessage.includes('ingredients') && lowerMessage.includes('instructions'));
  
  // Check if this is about modifying the CURRENT recipe
  // Include keywords for adding components like sauce, glaze, marinade
  const isModification = lowerUserMsg.includes('remove') || lowerUserMsg.includes('without') ||
                         lowerUserMsg.includes('substitute') || lowerUserMsg.includes('replace') ||
                         lowerUserMsg.includes('change') || lowerUserMsg.includes('add ') ||
                         lowerUserMsg.includes('modify') || lowerUserMsg.includes('update') ||
                         lowerUserMsg.includes('instead') || lowerUserMsg.includes('don\'t have') ||
                         lowerUserMsg.includes('doesn\'t have') || lowerUserMsg.includes('no ') ||
                         lowerUserMsg.includes('with a sauce') || lowerUserMsg.includes('with a glaze') ||
                         lowerUserMsg.includes('with a marinade') || lowerUserMsg.includes('make it with') ||
                         lowerUserMsg.includes('want a sauce') || lowerUserMsg.includes('like a sauce') ||
                         lowerUserMsg.includes('want to add') || lowerUserMsg.includes('would like to add') ||
                         lowerUserMsg.includes('incorporate') || lowerUserMsg.includes('include') ||
                         lowerMessage.includes('update recipe') || lowerMessage.includes('modified') ||
                         lowerMessage.includes('click') && lowerMessage.includes('update');
  
  // Check if user is explicitly asking for a SEPARATE/DIFFERENT dish
  const userAsksForSeparateDish = lowerUserMsg.includes('side dish') || 
                                   lowerUserMsg.includes('dessert') ||
                                   lowerUserMsg.includes('appetizer') ||
                                   lowerUserMsg.includes('what goes with') ||
                                   lowerUserMsg.includes('pair with') ||
                                   lowerUserMsg.includes('suggest a ') ||
                                   lowerUserMsg.includes('recommend a ');
  
  // Check if AI response indicates a NEW/different recipe (not a modification of current)
  // Be careful: "complement the flavors" is NOT the same as "to complement your meal"
  const isNewRecipeContext = lowerMessage.includes('here\'s a recipe for') || 
                      lowerMessage.includes('here is a recipe for') ||
                      lowerMessage.includes('here\'s a separate') ||
                      lowerMessage.includes('side dish') || 
                      (lowerMessage.includes('drink') && !lowerMessage.includes('drinking')) ||
                      lowerMessage.includes('cocktail') ||
                      lowerMessage.includes('martini') ||
                      (lowerMessage.includes('dessert') && !lowerMessage.includes('dessert-like')) ||
                      lowerMessage.includes('goes well with your') ||
                      lowerMessage.includes('to accompany your') ||
                      lowerMessage.includes('pairs well with');
  
  // Extract the RECIPE TITLE - look for bold text that appears after a blank line
  // or at the start of the recipe section (not inline mentions like "**Garlic Herb Sauce**")
  let messageRecipeTitle = '';
  
  // First try: Look for a title on its own line (standard recipe format)
  const titleOnOwnLine = message.match(/\n\s*\*\*([^*]{5,80})\*\*\s*\n/);
  if (titleOnOwnLine) {
    messageRecipeTitle = titleOnOwnLine[1].trim();
  } else {
    // Fallback: Look for bold text at the very start of a paragraph
    const titleAtStart = message.match(/(?:^|\n\n)\*\*([^*]{5,80})\*\*/);
    if (titleAtStart) {
      messageRecipeTitle = titleAtStart[1].trim();
    }
  }
  
  const currentTitleLower = currentRecipeTitle.toLowerCase().replace(/[^a-z0-9]/g, '');
  const messageTitleLower = messageRecipeTitle.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Title is "completely different" only if there's NO overlap with current recipe name
  // e.g., "Steak" -> "Steak with Sauce" = NOT different (it's an extension)
  // e.g., "Steak" -> "Lemon Tart" = completely different
  const isTitleCompletelyDifferent = messageRecipeTitle && currentRecipeTitle &&
    currentTitleLower.length > 5 && messageTitleLower.length > 5 &&
    !messageTitleLower.includes(currentTitleLower.substring(0, Math.min(10, currentTitleLower.length))) &&
    !currentTitleLower.includes(messageTitleLower.substring(0, Math.min(10, messageTitleLower.length)));
  
  // It's a NEW separate recipe ONLY if:
  // 1. User explicitly asked for a separate dish (side, dessert, etc.) AND
  // 2. The response has recipe content AND
  // 3. The title is completely different from current recipe
  if (userAsksForSeparateDish && hasRecipeContent && isTitleCompletelyDifferent) {
    return 'new';
  }
  
  // Also treat as new if AI clearly indicates it's a separate recipe AND title is different
  if (isNewRecipeContext && hasRecipeContent && isTitleCompletelyDifferent) {
    return 'new';
  }
  
  // It's an UPDATE to current recipe if:
  // 1. User asked for modification (sauce, substitute, add, etc.) OR
  // 2. Has recipe content and title is NOT completely different (it's the same or extended)
  if (isModification || (hasRecipeContent && !isTitleCompletelyDifferent)) {
    return 'update';
  }
  
  return null;
};

const DraggableChat = ({ recipe, onClose, onRecipeUpdate, onShowUpdatePreview }) => {
  // Default greeting message
  const getDefaultMessages = () => [
    {
      role: 'assistant',
      content: `Hi! I'm here to help you cook **${recipe.title}**. Ask me anything about the recipe, substitutions, cooking tips, or techniques! I can also help you create related recipes or modify this one.`,
    },
  ];

  const [messages, setMessages] = useState(getDefaultMessages);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chatLoading, setChatLoading] = useState(true);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false); // True full-screen for mobile
  const [foodPreferences, setFoodPreferences] = useState(null);
  const [measurementSystem, setMeasurementSystem] = useState(null);
  
  // Load user's measurement system preference
  useEffect(() => {
    const loadMeasurement = async () => {
      try {
        const res = await userApi.getMeasurementSystem();
        if (res.data?.measurement_system) {
          setMeasurementSystem(res.data.measurement_system);
        }
      } catch (e) { /* default to imperial */ }
    };
    loadMeasurement();
  }, []);
  const [initialViewportHeight, setInitialViewportHeight] = useState(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0); // Track keyboard height for iOS
  const [appliedMessageIndices, setAppliedMessageIndices] = useState(new Set()); // Track which messages have been applied
  const wasFullScreenBeforePrefsRef = useRef(false); // Track if user was in fullscreen before expanding prefs
  const foodPreferencesRef = useRef(null); // Ref to call save method
  const chatRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMessageCountRef = useRef(1);
  const isLoadingChatRef = useRef(false);
  const inputRef = useRef(null); // Ref for input element
  
  // Rotating loading phrase
  const loadingPhrase = useRotatingPhrase(loading);

  // Load chat from API on mount
  useEffect(() => {
    const loadChat = async () => {
      if (!recipe.recipe_id) {
        setChatLoading(false);
        return;
      }
      
      try {
        isLoadingChatRef.current = true;
        const response = await chatsApi.getByRecipeId(recipe.recipe_id);
        if (response.data && response.data.messages && response.data.messages.length > 0) {
          prevMessageCountRef.current = response.data.messages.length;
          setMessages(response.data.messages);
          setCurrentChatId(response.data.chat_id);
        }
      } catch (error) {
        console.error('Failed to load recipe chat:', error);
      } finally {
        isLoadingChatRef.current = false;
        setChatLoading(false);
      }
    };
    loadChat();
  }, [recipe.recipe_id]);

  // Auto-focus input on desktop only (not mobile - keyboard popup is jarring)
  useEffect(() => {
    const isMobileDevice = window.innerWidth < 768;
    if (!isMobileDevice && inputRef.current) {
      // Small delay to ensure component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, []); // Run once on mount

  // Save chat to API when messages change (debounced)
  useEffect(() => {
    if (chatLoading || messages.length <= 1 || isLoadingChatRef.current) return;
    
    const hasNewMessages = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;
    
    if (!hasNewMessages) return;
    
    const saveTimeout = setTimeout(async () => {
      try {
        if (currentChatId) {
          await chatsApi.update(currentChatId, { messages, update_timestamp: true });
        } else {
          const response = await chatsApi.create({ 
            messages,
            recipe_id: recipe.recipe_id
          });
          if (response.data && response.data.chat_id) {
            setCurrentChatId(response.data.chat_id);
          }
        }
      } catch (error) {
        console.error('Failed to save chat:', error);
      }
    }, 1000);
    
    return () => clearTimeout(saveTimeout);
  }, [messages, currentChatId, chatLoading, recipe.recipe_id]);

  // Capture initial viewport height before keyboard appears
  useEffect(() => {
    if (initialViewportHeight === null) {
      setInitialViewportHeight(window.innerHeight);
    }
  }, [initialViewportHeight]);

  // Handle iOS keyboard visibility using visualViewport API
  useEffect(() => {
    if (!isFullScreen) {
      setKeyboardHeight(0);
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      // When keyboard opens, visualViewport.height shrinks
      const heightDiff = window.innerHeight - viewport.height;
      // Only consider it a keyboard if diff is significant (> 100px)
      if (heightDiff > 100) {
        setKeyboardHeight(heightDiff);
        // Scroll to keep input visible
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        setKeyboardHeight(0);
      }
    };

    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, [isFullScreen]);

  // Load food preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await userApi.getFoodPreferences();
        if (response.data.success && response.data.preferences) {
          setFoodPreferences(response.data.preferences);
        }
      } catch (error) {
        console.error('Failed to load food preferences:', error);
      }
    };
    loadPreferences();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to clear chat history - now clears from API
  const handleClearChat = async () => {
    // Delete the chat from API if it exists
    if (currentChatId) {
      try {
        await chatsApi.delete(currentChatId);
      } catch (error) {
        console.error('Failed to delete chat:', error);
      }
    }
    setCurrentChatId(null);
    prevMessageCountRef.current = 1;
    setMessages(getDefaultMessages());
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('.no-drag')) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.current.x,
      y: touch.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await ai.chat(recipe.recipe_id, updatedMessages, foodPreferences, measurementSystem);
      const { message, success } = response.data;

      if (success) {
        const newMessages = [...updatedMessages, { role: 'assistant', content: message }];
        setMessages(newMessages);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64 = event.target.result.split(',')[1];
        
        setMessages((prev) => [...prev, { 
          role: 'user', 
          content: '📷 *Uploaded an image*' 
        }]);

        const response = await ai.analyzeImage(base64, 'recipe_card');
        const { recipe: analyzedRecipe, message, success } = response.data;

        if (success && analyzedRecipe) {
          const formattedRecipe = `**${analyzedRecipe.title}**\n\n*${analyzedRecipe.description || 'A delicious recipe'}*\n\n**Servings:** ${analyzedRecipe.servings || 'N/A'} | **Prep:** ${analyzedRecipe.prep_time || 'N/A'} min | **Cook:** ${analyzedRecipe.cook_time || 'N/A'} min\n\n**Ingredients:**\n${analyzedRecipe.ingredients.map(ing => `- ${ing.quantity} ${ing.unit} ${ing.name}`).join('\n')}\n\n**Instructions:**\n${analyzedRecipe.instructions.map(inst => `${inst.step_number}. ${inst.text}`).join('\n\n')}`;
          
          setMessages((prev) => [...prev, { role: 'assistant', content: formattedRecipe }]);
        } else {
          setMessages((prev) => [...prev, { 
            role: 'assistant', 
            content: message || "I couldn't extract a recipe from that image. Please try a clearer image!" 
          }]);
        }
      } catch (error) {
        console.error('Failed to analyze image:', error);
        setMessages((prev) => [...prev, { 
          role: 'assistant', 
          content: "Sorry, I had trouble analyzing that image. Please try again!" 
        }]);
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateRecipe = async (specificMessageIndex = null) => {
    if (loading) return;
    
    setLoading(true);
    // Auto-scroll to show loading spinner
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
    try {
      let conversationContext;
      let userModificationRequest = '';
      
      if (specificMessageIndex !== null) {
        // Only include conversation up to and including the specific message
        const relevantMessages = messages.slice(0, specificMessageIndex + 1);
        conversationContext = relevantMessages
          .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
          .join('\n\n');
        
        // Find the user message that triggered this assistant response
        for (let i = specificMessageIndex; i >= 0; i--) {
          if (messages[i].role === 'user') {
            userModificationRequest = messages[i].content;
            break;
          }
        }
      } else {
        // Build the complete conversation context
        conversationContext = messages
          .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
          .join('\n\n');
        
        // Get the last user message as the modification request
        const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
        userModificationRequest = lastUserMsg?.content || '';
      }
      
      // Check if the assistant's response contains a full recipe (has Ingredients and Instructions sections)
      const targetAssistantMsg = specificMessageIndex !== null 
        ? messages[specificMessageIndex] 
        : [...messages].reverse().find(m => m.role === 'assistant');
      
      const assistantHasFullRecipe = targetAssistantMsg?.content?.includes('**Ingredients:**') && 
                                     targetAssistantMsg?.content?.includes('**Instructions:**');
      
      // Build the prompt - different approach based on whether assistant provided full recipe or just acknowledged
      const extractPrompt = {
        role: 'user',
        content: assistantHasFullRecipe 
          ? `Extract the COMPLETE updated recipe from this assistant response as JSON.

ASSISTANT'S RECIPE:
${targetAssistantMsg.content}

CRITICAL TITLE RULES:
- Extract the COMPLETE recipe title exactly as it appears (e.g., "Heart-Healthy Classic Green Bean Casserole", NOT just "Heart Healthy")
- The title is usually a standalone line before "Servings:" or the description
- Include ALL descriptive words (like "Heart-Healthy", "Classic", "Spicy", "Reduced-Fat", etc.)
- Do NOT use section headers like "Ingredients" or "Instructions" as the title

Return as JSON with: title, description, ingredients (with name, quantity, unit, preparation, category for grouping like "For the Sauce"), instructions (with step_number, text, section for phase headers like "Make the Sauce"), recipe_tips, prep_time, cook_time, servings.`
          : `APPLY this modification to the recipe and return the COMPLETE UPDATED recipe as JSON.

ORIGINAL RECIPE:
Title: ${recipe.title}
Description: ${recipe.description || ''}
Category: ${recipe.category || 'other'}
Prep Time: ${recipe.prep_time || 0}
Cook Time: ${recipe.cook_time || 0}
Servings: ${recipe.servings || 0}

Ingredients:
${recipe.ingredients.map(ing => `${ing.category ? `[${ing.category}] ` : ''}- ${ing.quantity || ''} ${ing.unit || ''} ${ing.name}${ing.preparation ? ` (${ing.preparation})` : ''}`).join('\n')}

Instructions (with section/phase headers):
${recipe.instructions.map(inst => `${inst.section ? `[Section: ${inst.section}] ` : ''}${inst.step_number}. ${inst.text}`).join('\n')}

Tips: ${recipe.recipe_tips?.join(', ') || 'None'}

USER'S MODIFICATION REQUEST:
"${userModificationRequest}"

ASSISTANT'S ACKNOWLEDGMENT:
"${targetAssistantMsg?.content || ''}"

Apply the user's requested modification to the original recipe. Return the COMPLETE updated recipe as JSON with ALL ingredients and instructions (not just the changes). 
CRITICAL: Preserve ALL section/phase headers from the original recipe (like "Make the Garlic Herb Sauce", "Prepare the Steak", "Serve").
Include: title, description, ingredients (with name, quantity, unit, preparation, category for grouping), instructions (with step_number, text, section for phase headers), recipe_tips, prep_time, cook_time, servings.`,
      };
      
      // Use generate mode to get structured JSON
      const response = await ai.generateRecipe([extractPrompt], 'generate');
      const { recipe: updatedRecipe, success } = response.data;

      if (success && updatedRecipe) {
        // Clean up title - remove any "Title:" prefix the AI may have included
        if (updatedRecipe.title && updatedRecipe.title.toLowerCase().startsWith('title:')) {
          updatedRecipe.title = updatedRecipe.title.substring(6).trim();
        }
        
        // IMPORTANT: Preserve original category - regex parser may incorrectly guess category
        // Only change category if user explicitly requested it (e.g., "make this a dessert")
        const userAskedForCategoryChange = userModificationRequest?.toLowerCase().match(
          /\b(make|change|convert).*(to|into)\s*(a\s+)?(dessert|appetizer|main|side|breakfast|soup|salad|beverage|snack)/i
        );
        if (!userAskedForCategoryChange && recipe.category) {
          updatedRecipe.category = recipe.category;
        }
        
        // Extract the user's modification request and summarize it for the tweak description
        let modificationDescription = null;
        
        if (userModificationRequest) {
          try {
            // Use dedicated AI summarization endpoint for clean description
            const summarizeResponse = await ai.summarizeText(
              userModificationRequest,
              `Summarize this recipe modification request into ONE clean, concise description (e.g., "Added powdered sugar topping" or "Reduced sugar by half"). Fix any typos. Return ONLY the summary text, no quotes or explanation.`
            );
            
            const responseText = summarizeResponse.data.summary || '';
            modificationDescription = responseText.trim().replace(/^["']|["']$/g, ''); // Remove quotes if any
          } catch (summarizeError) {
            console.error('Failed to summarize modification:', summarizeError);
            // Fallback to cleaned up version of user's message
            modificationDescription = userModificationRequest.slice(0, 100);
          }
        }
        
        // Directly save the updated recipe (title changes are handled by AI based on conflict rules)
        const result = await onShowUpdatePreview(updatedRecipe, recipe.title, modificationDescription);
        
        if (result?.success) {
          // Mark the message as applied so button disappears
          if (specificMessageIndex !== null) {
            setAppliedMessageIndices(prev => new Set([...prev, specificMessageIndex]));
          }
          
          // Trigger background enhancement for aisle updates (ingredients may have changed)
          try {
            await recipeApi.enhanceWithAisles(recipe.recipe_id);
          } catch (enhanceError) {
            console.log('Background enhancement queued:', enhanceError.message);
          }
          
          // Add confirmation message to chat based on what changed
          let confirmMessage;
          if (result.titleChanged) {
            confirmMessage = `✅ **Title & Recipe Updated!**`;
          } else {
            confirmMessage = `✅ **Recipe Updated!**`;
          }
          setMessages(prev => [...prev, { role: 'assistant', content: confirmMessage }]);
        } else {
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: "❌ Sorry, I couldn't save the changes. Please try again." 
          }]);
        }
      } else {
        alert('Failed to extract recipe updates. Please try rephrasing your request.');
      }
    } catch (error) {
      console.error('Failed to update recipe:', error);
      alert('Failed to update recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUpdatedRecipe = async (updatedRecipeData) => {
    try {
      await onRecipeUpdate(updatedRecipeData);
      alert('Recipe updated successfully! The page will refresh.');
      window.location.reload();
    } catch (error) {
      console.error('Failed to save updated recipe:', error);
      alert('Failed to save recipe. Please try again.');
    }
  };

  const handleAddNewRecipe = async (specificMessageIndex = null) => {
    if (loading) return;
    
    setLoading(true);
    try {
      let targetMessage;
      
      if (specificMessageIndex !== null) {
        // Use the specific message
        targetMessage = messages[specificMessageIndex];
      } else {
        // Find the last recipe in the conversation
        targetMessage = messages.filter(m => m.role === 'assistant').pop();
      }
      
      if (!targetMessage || !targetMessage.content.includes('**Ingredients:**')) {
        alert('Please have a complete recipe in the conversation first.');
        setLoading(false);
        return;
      }
      
      // Parse the recipe directly from markdown - no AI call needed!
      // Strip metadata before parsing
      const content = stripRecipeMeta(targetMessage.content);
      
      // Try to get title from AI metadata first
      const meta = parseRecipeMeta(targetMessage.content);
      
      try {
        // Extract title: prefer AI metadata, then regex fallback
        let title;
        if (meta && meta.title && meta.title.trim()) {
          // Use AI-provided title (most reliable)
          title = meta.title.trim();
        } else {
          // Fallback to regex extraction (for old messages without metadata)
          // Try bold title first: **Recipe Title**
          const boldTitleMatch = content.match(/\*\*([^*]{5,80})\*\*/);
          // Then try text between double newlines (but NOT numbered lines like instructions)
          const plainTitleMatch = content.match(/\n\n([^0-9\n*][^\n]{4,80})\n\n/);
          
          if (boldTitleMatch && !boldTitleMatch[1].toLowerCase().includes('ingredients') && 
              !boldTitleMatch[1].toLowerCase().includes('instructions')) {
            title = boldTitleMatch[1].trim();
          } else if (plainTitleMatch && !plainTitleMatch[1].match(/^\d+\./)) {
            title = plainTitleMatch[1].trim();
          } else {
            title = 'Untitled Recipe';
          }
        }
        
        // Extract description (text after title, before Servings)
        const descMatch = content.match(new RegExp(`${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?\\n\\n([^\\n]+)\\n\\n\\*\\*?Servings`));
        const description = descMatch ? descMatch[1].trim() : '';
        
        // Extract servings, prep, cook times
        const servingsMatch = content.match(/Servings:\s*(\d+)/i);
        const prepMatch = content.match(/Prep:\s*(\d+)/i);
        const cookMatch = content.match(/Cook:\s*(\d+)/i);
        
        // Extract ingredients section - capture everything up to Instructions
        const ingredientsMatch = content.match(/\*\*Ingredients:\*\*\s*([\s\S]*?)(?=\*\*Instructions:\*\*)/i);
        const ingredientsText = ingredientsMatch ? ingredientsMatch[1] : '';
        
        // Parse ingredients - filter out section headers but keep ingredient lines
        const ingredientLines = ingredientsText.split('\n').filter(line => {
          const trimmed = line.trim();
          if (!trimmed) return false;
          // Skip section headers like "**For the Sauce:**" or "*For the Sauce:*"
          if (trimmed.match(/^\*+[^*]+:\*+$/)) return false;
          // Skip lines that are ONLY bold text (headers)
          if (trimmed.match(/^\*\*[^*]+\*\*$/)) return false;
          return true;
        });
        const ingredients = ingredientLines.map(line => {
          // Remove bullet points and strip markdown bold markers
          let cleaned = line.replace(/^[-•*]\s*/, '').trim();
          cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1'); // Remove ** from ingredient names
          
          // Try to parse quantity/unit/name
          const qtyMatch = cleaned.match(/^([\d\/½¼¾⅓⅔⅛]+(?:\s*[-–]\s*[\d\/½¼¾⅓⅔⅛]+)?)\s*(cups?|tablespoons?|tbsp|teaspoons?|tsp|ounces?|oz|pounds?|lbs?|cloves?|large|medium|small|pinch)?\s*(.+)/i);
          if (qtyMatch) {
            return {
              name: qtyMatch[3].replace(/\*\*([^*]+)\*\*/g, '$1').trim(),
              quantity: qtyMatch[1].trim(),
              unit: qtyMatch[2] ? qtyMatch[2].trim() : '',
              category: null,
              preparation: null,
              aisle: 'other'
            };
          }
          return { name: cleaned, quantity: '', unit: '', category: null, preparation: null, aisle: 'other' };
        });
        
        // Extract instructions section
        const instructionsMatch = content.match(/\*\*Instructions:\*\*\s*([\s\S]*?)(?=\*\*Tips|Tips & Tricks|\n\nNutrition|$)/i);
        const instructionsText = instructionsMatch ? instructionsMatch[1] : '';
        
        // Parse instructions - filter out section headers and only keep numbered steps
        const instructionLines = instructionsText.split('\n').filter(line => {
          const trimmed = line.trim();
          if (!trimmed) return false;
          // Skip section headers like "*Preheat:*" or "Preheat:"
          if (trimmed.match(/^\*[^*]+:\*$/)) return false;
          if (trimmed.match(/^[A-Z][^:]+:$/) && trimmed.length < 30) return false;
          // Skip bold headers like "**Preheat:**"
          if (trimmed.match(/^\*\*[^*]+:\*\*$/)) return false;
          // Only keep lines that start with a number (actual steps)
          return trimmed.match(/^\d+\./);
        });
        
        const instructions = instructionLines.map((line, i) => {
          // Remove the leading number and any duplicate numbers
          let cleaned = line.replace(/^\d+\.\s*/, '').trim();
          // Remove any remaining leading numbers (handles "1. 1. Step" case)
          cleaned = cleaned.replace(/^\d+\.\s*/, '').trim();
          // Remove markdown bold
          cleaned = cleaned.replace(/\*\*([^*]+)\*\*/g, '$1');
          if (!cleaned) return null;
          return {
            step_number: i + 1,
            text: cleaned,
            section: null,
            duration_minutes: null
          };
        }).filter(Boolean);
        
        // Renumber steps
        instructions.forEach((inst, i) => { inst.step_number = i + 1; });
        
        // Extract tips
        const tipsMatch = content.match(/\*\*Tips & Tricks:\*\*\s*([\s\S]*?)(?=\*\*Storage|\*\*Nutrition|Nutrition \(|$)/i);
        const tipsText = tipsMatch ? tipsMatch[1] : '';
        const tipLines = tipsText.split('\n').filter(line => line.trim().startsWith('-') || line.trim().match(/^[A-Z]/));
        const recipe_tips = tipLines.slice(0, 5).map(t => t.replace(/^[-•*]\s*/, '').trim()).filter(Boolean);
        
        // Extract nutrition
        const nutritionMatch = content.match(/Nutrition.*?Calories:\s*([\d]+).*?Carbs:\s*([\d]+).*?Protein:\s*([\d]+).*?Fat:\s*([\d]+)/i);
        const nutrition = nutritionMatch ? {
          calories: nutritionMatch[1],
          carbohydrates: `${nutritionMatch[2]}g`,
          protein: `${nutritionMatch[3]}g`,
          fat: `${nutritionMatch[4]}g`
        } : null;
        
        const newRecipe = {
          title,
          description,
          ingredients,
          instructions,
          recipe_tips,
          prep_time: prepMatch ? parseInt(prepMatch[1]) : null,
          cook_time: cookMatch ? parseInt(cookMatch[1]) : null,
          servings: servingsMatch ? parseInt(servingsMatch[1]) : 4,
          nutrition,
          cuisine_type: null,
          difficulty_level: 'medium',
          category: 'other',
          tags: [],
          generation_method: 'chat'
        };
        
        // Create the recipe directly - no AI call! (instant save)
        const createResponse = await recipeApi.create(newRecipe);
        const newRecipeId = createResponse.data.recipe_id;
        
        // Trigger background enhancement for aisles (don't wait for it)
        recipeApi.enhanceWithAisles(newRecipeId).catch(err => {
          console.log('Background aisle enhancement failed (non-critical):', err);
        });
        
        // Navigate immediately - user doesn't wait for AI
        window.location.href = `/recipe/${newRecipeId}`;
      } catch (parseError) {
        console.error('Failed to parse recipe, trying AI fallback:', parseError);
        // Fallback to AI if parsing fails
        try {
          const extractPrompt = {
            role: 'user',
            content: `Convert this recipe to structured JSON format:\n\n${content}`,
          };
          const response = await ai.generateRecipe([extractPrompt], 'generate');
          const { recipe: newRecipe, success } = response.data;
          if (success && newRecipe) {
            const createResponse = await recipeApi.create({
              ...newRecipe,
              generation_method: 'chat',
            });
            const newRecipeId = createResponse.data.recipe_id;
            
            // Trigger background enhancement (don't wait for it)
            recipeApi.enhanceWithAisles(newRecipeId).catch(err => {
              console.log('Background recipe enhancement failed (non-critical):', err);
            });
            
            window.location.href = `/recipe/${newRecipeId}`;
          } else {
            alert('Failed to extract recipe. Please try again.');
          }
        } catch (aiError) {
          console.error('AI fallback also failed:', aiError);
          alert('Failed to add recipe. Please try again.');
        }
      }
    } catch (error) {
      console.error('Failed to add recipe:', error);
      alert('Failed to add recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Determine if we're on mobile (for full-screen behavior)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Toggle full-screen mode (mobile gets true fullscreen, desktop gets expanded but not full height)
  const handleToggleExpand = () => {
    if (isMobile) {
      setIsFullScreen(!isFullScreen);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  // Get the current display mode
  const isCurrentlyExpanded = isMobile ? isFullScreen : isExpanded;

  return (
    <div
      ref={chatRef}
      className={`${
        isFullScreen
          ? 'fixed inset-0 z-[9999] rounded-none' // True full-screen for mobile
          : isExpanded
          ? 'fixed z-50 left-0 right-0' // Desktop expanded: full width but not full height
          : 'draggable-chat bg-white rounded-t-lg shadow-2xl w-full md:w-96 max-w-full'
      } bg-white shadow-2xl flex flex-col`}
      style={
        isFullScreen
          ? {
              // Full viewport coverage - adjust for keyboard on iOS
              width: '100vw',
              height: keyboardHeight > 0 ? `calc(100vh - ${keyboardHeight}px)` : '100vh',
              maxHeight: keyboardHeight > 0 ? `calc(100vh - ${keyboardHeight}px)` : '100vh',
              transition: 'height 0.1s ease-out',
            }
          : isExpanded
          ? { 
              bottom: '64px', 
              top: 'auto',
              height: '60vh', // Desktop: ~60% of viewport height
              maxHeight: '600px', // Cap at 600px max
              minHeight: '400px', // Minimum usable height
            }
          : {
              transform: `translate(${position.x}px, ${position.y}px)`,
              cursor: isDragging ? 'grabbing' : 'auto',
            }
      }
    >
      {/* Drag Handle / Header - entire header is clickable to toggle expand */}
      <div
        onMouseDown={!isCurrentlyExpanded && !isMobile ? handleMouseDown : undefined}
        onTouchStart={!isCurrentlyExpanded ? handleTouchStart : undefined}
        onClick={handleToggleExpand}
        className={`bg-amber-600 text-white px-4 py-3 ${
          isFullScreen ? 'rounded-none' : isExpanded ? 'rounded-t-lg' : 'rounded-t-lg cursor-grab active:cursor-grabbing'
        } flex items-center justify-between flex-shrink-0 cursor-pointer`}
      >
        <div className="flex items-center gap-2">
          {!isCurrentlyExpanded && !isMobile && <GripHorizontal size={20} />}
          <h3 className="font-semibold">{isFullScreen ? 'Recipe Assistant' : 'Cook-Along Assistant'}</h3>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {messages.length > 1 && (
            <button
              onClick={handleClearChat}
              className="no-drag px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 rounded transition-colors"
              title="Clear chat history"
            >
              Clear
            </button>
          )}
          <button
            onClick={handleToggleExpand}
            data-testid="toggle-expand-button"
            className="no-drag p-1 hover:bg-amber-700 rounded transition-colors"
            title={isCurrentlyExpanded ? 'Exit Full Screen' : 'Full Screen'}
          >
            {isCurrentlyExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
          <button
            onClick={onClose}
            data-testid="close-chat-button"
            className="no-drag p-1 hover:bg-amber-700 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Food Preferences bar - Always visible below header */}
      <div className="flex-shrink-0">
        <FoodPreferences 
          ref={foodPreferencesRef}
          onPreferencesChange={setFoodPreferences}
          compact={true}
          defaultExpanded={false}
          onExpand={isMobile ? () => {
            // Remember if user was already in fullscreen before expanding
            wasFullScreenBeforePrefsRef.current = isFullScreen;
            // On mobile, auto-switch to fullscreen when expanding preferences
            if (!isFullScreen) {
              setIsFullScreen(true);
            }
          } : undefined}
          onCollapse={isMobile ? () => {
            // On mobile, return to original state when collapsing preferences
            // If started from mini mode, go back to mini mode
            // If started from fullscreen, stay in fullscreen
            if (!wasFullScreenBeforePrefsRef.current && isFullScreen) {
              setIsFullScreen(false);
            }
          } : undefined}
        />
      </div>

      {/* Messages */}
      <div className={`${isFullScreen ? 'flex-1 min-h-0' : isExpanded ? 'flex-1' : 'chat-messages'} p-4 space-y-4 bg-gray-50 overflow-y-auto relative`}>
        {messages.map((msg, idx) => {
          // Get the previous user message for context detection
          const previousUserMsg = idx > 0 && messages[idx - 1]?.role === 'user' 
            ? messages[idx - 1].content 
            : '';
          
          // Try to get AI-generated metadata first, fall back to detection logic
          const meta = msg.role === 'assistant' ? parseRecipeMeta(msg.content) : null;
          
          // Determine recipe action type: prefer metadata, fall back to detection
          let recipeType = null;
          if (msg.role === 'assistant' && idx > 0) {
            if (meta && meta.action && meta.action !== 'none') {
              // Use AI-provided action type
              recipeType = meta.action; // 'update' or 'new'
            } else if (!meta) {
              // No metadata (old message or edge case) - use existing detection logic
              recipeType = detectRecipeAction(msg.content, previousUserMsg, recipe.title);
            }
            // If meta.action === 'none', recipeType stays null (no button)
          }
          
          // Strip metadata from content for display
          const displayContent = stripRecipeMeta(msg.content);
          
          return (
            <div key={idx}>
              <div
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                    msg.role === 'user'
                      ? 'bg-amber-600 text-white'
                      : 'bg-white text-gray-900 shadow-sm'
                  }`}
                >
                  <div className="leading-snug">
                    <ReactMarkdown components={chatMarkdownComponents}>
                      {displayContent}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
              
              {/* Inline action button for this specific message - hide if already applied */}
              {recipeType && !appliedMessageIndices.has(idx) && (
                <div className="flex justify-start ml-2 mt-2 mb-4">
                  {recipeType === 'update' ? (
                    <button
                      onClick={() => handleUpdateRecipe(idx)}
                      disabled={loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw size={12} />
                      Apply This Change
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAddNewRecipe(idx)}
                      disabled={loading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors disabled:opacity-50"
                    >
                      📥 Add to Recipe Box
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white px-3 py-2 rounded-lg shadow-sm flex items-center gap-2 text-sm">
              <Loader className="animate-spin" size={16} />
              <span>{loadingPhrase}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - auto-growing textarea */}
      <div className={`no-drag p-3 border-t bg-white flex-shrink-0 ${isFullScreen ? 'pb-safe' : ''}`}
           style={isFullScreen && keyboardHeight > 0 ? { paddingBottom: '12px' } : isFullScreen ? { paddingBottom: 'max(12px, env(safe-area-inset-bottom))' } : {}}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onFocus={() => {
              // On iOS, scroll messages to bottom when keyboard appears
              if (isFullScreen) {
                setTimeout(() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }
            }}
            onKeyDown={(e) => {
              // Submit on Enter (without Shift)
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={messages.length > 1 ? "Any changes?" : "Ask me anything..."}
            data-testid="chat-message-input"
            className="flex-1 px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none overflow-hidden"
            style={{ fontSize: '16px', minHeight: '40px', maxHeight: '120px' }}
            disabled={loading}
            rows={1}
          />
          <label className="flex items-center justify-center p-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors h-10">
            <Image size={16} className="text-gray-600" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              disabled={loading}
            />
          </label>
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            data-testid="send-chat-message-button"
            className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-10"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraggableChat;